import React, { useState, useEffect, useContext } from "react";
import "../styles/styles.css";
import "../../../styles/global.css";
import AccreditationButton from "../../AccreditationButton/AccreditationButton";
import AccreditationInput from "../../AccreditationInput/AccreditationInput";
import { icons } from "../../../constants";
import { apiRoutes } from "../../../utils/apiRoutes";
import {
  createData,
  updateData,
  fetchData,
  deleteData,
} from "../../../utils/methods";
import { validateString } from "../../../utils/validateString";
import AddPicture from "./AddPicture";
import { useNavigate } from "react-router-dom";
import { TranslationsContext } from "../../../utils/TranslationsContext";
import { formatDateForInput } from "../../../utils/formatDateForInput";
import { trimValues } from "../../../utils/trimValues";
import CustomImageCropper from "../../ImageCropper/ImageCropper";
import { useAlert } from "../../../utils/AlertContext";
import CustomDateRangePicker from "../../CustomDateRangePicker/CustomDateRangePicker";
import { useTheme } from "../../../utils/ThemeContext";
import HistoryPanel from "../../HistoryPanel/HistoryPanel";
import PredlogPanel from "../../PredlogPanel/PredlogPanel";
import CommentsPanel from "../../Comments/components/CommentsPanel";
import EditModalShell from "./EditModalShell";
import RegisteredByLine from "./RegisteredByLine";
import ConfirmationModal from "./ConfirmationModal";
import Modal from "../Modal";
import useEnterKeySubmit from "../../../hooks/useEnterKeySubmit";
import { diffChangedFields } from "../../../utils/diffChangedFields";
import { useAvatarRegistry } from "../../../utils/AvatarContext";

// A build-time literal (not apiRoutes, which is parsed from JSON at module
// load and so can't be folded away) — lets Terser prove tier-exclusive
// branches below are dead code, so e.g. PredlogPanel/CommentsPanel/
// HistoryPanel genuinely aren't present in a tier's compiled bundle, not
// just hidden at runtime.
const TIER = process.env.REACT_APP_MODE;

const AddPerson = ({
  onCloseClick,
  object,
  editing,
  event_id,
  handleAddData,
  handleUpdateData,
  handleDeleteData,
  crop,
}) => {
  const { t } = useContext(TranslationsContext);
  const { theme } = useTheme();
  const ID_CARD_AND_PASSPORT = [
    { key: 1, value: "licna_karta", label: t("IDENTITY_CARD") },
    { key: 2, value: "pasos", label: t("PASSPORT") },
  ];
  const ID_CARD_AND_NATIONAL_ID = [
    { key: 1, value: "licna_karta", label: t("IDENTITY_CARD") },
    { key: 2, value: "jmbg", label: t("JMBG") },
  ];

  const PRIVILEGED_COUNTRIES = [
    "COUNTRY_BOSNIA_AND_HERZEGOVINA",
    "COUNTRY_MONTENEGRO",
  ];
  const [cropImage, setCropImage] = useState(false);
  const [imageForCrop, setImageForCrop] = useState();
  const [cropKey, setCropKey] = useState(0);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false);
  const { showAlert } = useAlert();
  const { setCached: setCachedAvatar } = useAvatarRegistry();
  const [personObject, setPersonObject] = useState(
    object
      ? object
      : {
          slika: "",
          ime: "",
          prezime: "",
          entitet: "",
          pozicija: "",
          email: "",
          tip_dokumenta: ID_CARD_AND_NATIONAL_ID[0].value,
          broj_dokumenta: "",
          dogadjajId: event_id,
          slika_crop: "",
          drzavljanstvo: "",
        },
  );


  useEffect(() => {
    const language = localStorage.getItem("language") || "sr";

    const fetchCountries = async () => {
      try {
        setIsLoading(true);

        const result = await fetchData(
          `${apiRoutes.getCountries}?language=${language}`,
          showAlert,
          navigate,
          t,
        );
        if (!result) return;

        setCountries(result.data);
      } catch (err) {
        console.error("Error fetching countries:", err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchCountries();
  }, []);

  useEffect(() => {
    const fetchPerson = async () => {
      try {
        setIsLoading(true);

        const result = await fetchData(
          apiRoutes.getPersonById.replace(":personId", object.id),
          showAlert,
          navigate,
          t,
        );

        if (!result) return;

        const {
          slika_crop,
          komentari,
          predlozi,
          unreadKomentariCount,
          dogadjaj,
          status,
          createdAt,
          updatedAt,
        } = result.data || {};
        setPersonObject((prev) => ({
          ...prev,
          slika_crop,
          komentari,
          predlozi,
          unreadKomentariCount,
          dogadjaj,
          status,
          createdAt,
          updatedAt,
        }));
      } catch (err) {
        console.error("Error fetching person:", err);
      } finally {
        setIsLoading(false);
      }
    };

    // Also needed for Predlozi/Comments/History in the side panel, not just
    // the photo — so this always refetches on mount for editing, not only
    // when slika_crop happens to be missing.
    if (editing) {
      fetchPerson();
    }
  }, []);

  useEffect(() => {
    if (personObject.drzavljanstvo === "COUNTRY_SERBIA") {
      if (personObject.tip_dokumenta === "jmbg") {
        setInputValue("jmbg", personObject.broj_dokumenta);
      } else {
        setInputValue("br_licne_karte", personObject.broj_dokumenta);
        setInputValue("datum_rodjenja", personObject.datum_rodjenja);
      }
    }
  }, []);

  const [errors, setErrors] = useState({});
  const [imageLoading, setImageLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [countries, setCountries] = useState([]);
  const [isReapprovalConfirmOpen, setIsReapprovalConfirmOpen] = useState(false);

  const navigate = useNavigate();

  const setInputValue = (key, value) => {
    setPersonObject((prevValues) => ({
      ...prevValues,
      [key]: value,
    }));
  };

  const validateAllFields = () => {
    const isSerbia = personObject.drzavljanstvo === "COUNTRY_SERBIA";
    const isJmbgSelected = isSerbia && personObject.jmbg;
    const shouldShowIssuanceFields =
      personObject.drzavljanstvo &&
      (!isSerbia ||
        (isSerbia &&
          !personObject.jmbg &&
          (personObject.br_licne_karte || personObject.datum_rodjenja)));

    const fieldsToValidate = [
      "ime",
      "prezime",
      "slika",
      "broj_dokumenta",
      "email",
      "jmbg",
      "drzavljanstvo",
      // Only rendered/collected on the Serbia ID-card path (see the
      // drzavljanstvo === "COUNTRY_SERBIA" branch below) — requiring it
      // unconditionally failed every non-Serbian person with no visible
      // field to blame, since nothing outside that branch ever shows it.
      ...(isSerbia ? ["datum_rodjenja"] : []),
      ...(shouldShowIssuanceFields
        ? ["zemlja_izdavanja", "datum_izdavanja", "datum_isteka"]
        : []),
      "tip_dokumenta",
    ];
    let hasError = false;
    let err = false;
    fieldsToValidate.forEach((field) => {
      let value = personObject[field];
      if (field === "broj_dokumenta") {
        err = validateString(value, "number");
      } else if (field === "jmbg") {
        if (personObject.jmbg && personObject.jmbg !== "") {
          err = validateString(value, "jmbg");
        } else {
          err = false;
          value = "1";
        }
      } else if (field === "datum_izdavanja") {
        const izdavanje = personObject.datum_izdavanja;
        const istek = personObject.datum_isteka;

        if (izdavanje && istek) {
          const datumIzdavanja = new Date(izdavanje);
          const datumIsteka = new Date(istek);

          if (!isNaN(datumIzdavanja) && !isNaN(datumIsteka)) {
            if (datumIzdavanja >= datumIsteka) {
              console.warn("Datum izdavanja mora biti pre datuma isteka");

              setErrors((prevErrors) => ({
                ...prevErrors,
                datum_izdavanja: true,
                datum_isteka: true,
              }));

              hasError = true;
              return;
            }
          }
        }
      } else if (field === "datum_rodjenja") {
        if (personObject.jmbg) {
          err = false;
          value = "1";
        }
      } else if (field === "email") {
        if (value !== "") {
          err = validateString(value, "e_mail");
        } else {
          return;
        }
      }
      if (!value || err) {
        setErrors((prevErrors) => ({
          ...prevErrors,
          [field]: true,
        }));
        hasError = true;
      }
    });

    return !hasError;
  };

  const handleAddClick = async () => {
    if (!personObject.slika) {
      showAlert(t("ERROR_PHOTO_REQUIRED"), "error");
      return;
    }

    if (personObject.drzavljanstvo === "" && countries.length === 0) {
      showAlert(t("CITIZENSHIP_INPUT_FIELD_ERROR"), "error");
      return;
    }

    if (personObject.drzavljanstvo === "COUNTRY_SERBIA") {
      if (personObject.jmbg) {
        personObject.tip_dokumenta = "jmbg";
        personObject.broj_dokumenta = personObject.jmbg;
      } else {
        personObject.tip_dokumenta = "licna_karta";
        personObject.broj_dokumenta = personObject.br_licne_karte;
      }
    }

    setErrors();
    const isValid = validateAllFields();
    if (!isValid) {
      console.log("Postoje greške u formi.");
      showAlert(t("ERROR_MISSING_REQUIRED_FIELDS"), "error");
      return;
    }

    console.log("errors", errors);
    console.log("countries.length", countries.length);

    setIsLoading(true);

    if (personObject.drzavljanstvo === "COUNTRY_SERBIA") {
      delete personObject.br_licne_karte;
      delete personObject.jmbg;
      if (personObject.jmbg && personObject.jmbg !== "") {
        console.log("273");
        delete personObject.datum_rodjenja;
      }
    }

    personObject.slika_avatar = personObject.slika_crop;

    const sendData = trimValues(personObject);

    try {
      const result = await createData(
        apiRoutes.addPerson,
        sendData,
        showAlert,
        navigate,
        t,
      );
      if (!result) return;

      console.log("Podaci su uspešno sačuvani:", result.data);
      handleAddData({
        ...sendData,
        id: result.data?.id,
        slika_avatar: personObject.slika_crop,
      });
      setCachedAvatar(result.data?.id, personObject.slika_crop);
      showAlert(t("SUCCESSFULLY_SAVED"), "success");
      onCloseClick();
    } catch (error) {
      console.error("API poziv nije uspeo:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveClick = async () => {
    console.log("personObject save", personObject);
    if (personObject.drzavljanstvo === "" && countries.length === 0) {
      showAlert(t("CITIZENSHIP_INPUT_FIELD_ERROR"), "error");
      return;
    }

    if (personObject.drzavljanstvo === "COUNTRY_SERBIA") {
      if (personObject.jmbg) {
        personObject.tip_dokumenta = "jmbg";
        personObject.broj_dokumenta = personObject.jmbg;
      } else {
        personObject.tip_dokumenta = "licna_karta";
        personObject.broj_dokumenta = personObject.br_licne_karte;
      }
    }

    setErrors();
    const isValid = validateAllFields();
    if (!isValid) {
      console.log("Postoje greške u formi.", errors);
      showAlert(t("ERROR_MISSING_REQUIRED_FIELDS"), "error");
      return;
    }

    // Saving here always drops status back to prijavljen server-side (see
    // updateOsoba) since no explicit status is sent — for an already
    // approved person that silently invalidates their accreditation, so
    // make them confirm before it happens instead of surprising them.
    if (personObject.status === "odobren") {
      setIsReapprovalConfirmOpen(true);
      return;
    }

    await performSave();
  };

  const performSave = async () => {
    const id = object.id;
    const route = apiRoutes.updatePerson.replace(":personId", id);

    if (personObject.drzavljanstvo === "COUNTRY_SERBIA") {
      if (personObject.jmbg && personObject.jmbg !== "") {
        console.log("331");
        delete personObject.br_licne_karte;
        delete personObject.jmbg;
        delete personObject.datum_rodjenja;
      } else {
        delete personObject.br_licne_karte;
        delete personObject.jmbg;
      }
    }

    const podaciZaSlanje = { ...personObject };
    delete podaciZaSlanje.id;
    // Display-only fields merged in by the fetchPerson effect (for the
    // Comments/Predlog/History side panels) — not updateOsoba arguments.
    // old_person (the /persons list snapshot) never had these keys, so the
    // diff below would otherwise flag them as "changed" and send them,
    // which gs.rest's buildMutation can't type and rejects outright.
    delete podaciZaSlanje.komentari;
    delete podaciZaSlanje.predlozi;
    delete podaciZaSlanje.dogadjaj;
    delete podaciZaSlanje.unreadKomentariCount;
    delete podaciZaSlanje.updatedAt;

    const objekatPromena = diffChangedFields(
      podaciZaSlanje,
      personObject.old_person,
      ["old_person"],
    );
    const sendData = trimValues(objekatPromena);
    console.log("promenjena polja 1", objekatPromena);

    if (personObject.broj_dokumenta) {
      sendData.broj_dokumenta = personObject.broj_dokumenta;
    }

    setIsLoading(true);

    try {
      const result = await updateData(route, sendData, showAlert, navigate, t);
      if (!result) return;

      console.log("Podaci su uspešno sačuvani:", result.data);
      handleUpdateData({
        ...sendData,
        id: result.data?.id,
        slika_avatar: personObject.slika_crop,
        status: "prijavljen",
      });
      setCachedAvatar(object.id, personObject.slika_crop);
      showAlert(t("SUCCESSFULLY_SAVED"), "success");
      onCloseClick();
    } catch (error) {
      console.error("API poziv nije uspeo:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleConfirmReapproval = () => {
    setIsReapprovalConfirmOpen(false);
    performSave();
  };

  const handleStatusChange = async (value) => {
    setIsLoading(true);
    try {
      const response = await createData(
        apiRoutes.updatePersonStatus,
        { status: value, id: personObject.id },
        showAlert,
        navigate,
        t,
      );
      if (!response) return;

      setPersonObject((prev) => ({ ...prev, status: value }));
      handleUpdateData?.({ status: value, id: personObject.id });
      showAlert(t("SUCCESSFULLY_SAVED") || "Uspešno sačuvano", "success");
    } catch (error) {
      console.error("API poziv nije uspeo:", error.message);
    } finally {
      setIsLoading(false);
    }
  };

  const handleDeleteClick = async () => {
    const route = apiRoutes.deletePerson.replace(":personId", object.id);
    setIsLoading(true);
    try {
      const result = await deleteData(route, null, showAlert, navigate, t);
      if (!result) return;

      handleDeleteData?.(object.id);
      showAlert(t("SUCCESSFULLY_DELETED"), "success");
      onCloseClick();
    } catch (error) {
      console.error("API poziv nije uspeo:", error.message);
    } finally {
      setIsLoading(false);
      setIsConfirmDeleteOpen(false);
    }
  };

  const isResolved =
    personObject.status === "odobren" || personObject.status === "odbijen";

  // Second-tier (verifier) users can vote/comment but never alter person
  // info at all — apiRoutes.updatePerson is absent there. Kept separate
  // from the broader `readOnly` below because it specifically means "this
  // is a verifier, not an admin reviewing everyone's votes" — PredlogPanel
  // uses it to stay visible for Third even once a person is resolved.
  const isSecondTierViewer = editing && !apiRoutes.updatePerson;

  // Same read-only treatment as the Second-tier case, extended to Second
  // and Third once accreditation is resolved (approved/rejected) — Third
  // reopens editing deliberately via the status pill (which resets status
  // itself), so locking the form otherwise prevents accidental edits to a
  // decided record. First has no status pill at all (TIER !== "first" gates
  // it out) — for First, editing a resolved person *is* the unlock: saving
  // has no explicit status in the payload, so the backend naturally resets
  // it to "prijavljen" and the person goes back through review. So First stays
  // editable even when resolved, on purpose.
  const readOnly =
    isSecondTierViewer || (editing && isResolved && TIER !== "first");

  const statusPillConfig =
    personObject.status === "odobren"
      ? { bg: "var(--Green-box)", icon: icons.accept, label: t("APPROVED") }
      : personObject.status === "odbijen"
        ? { bg: "var(--Red-box)", icon: icons.decline, label: t("REJECTED") }
        : null;

  useEnterKeySubmit(
    readOnly ? () => {} : editing ? handleSaveClick : handleAddClick,
  );

  useEffect(() => {
    const isNotPrivilegedAndNotSerbia =
      !PRIVILEGED_COUNTRIES.includes(personObject.drzavljanstvo) &&
      personObject.drzavljanstvo !== "COUNTRY_SERBIA" &&
      personObject.drzavljanstvo !== "";
    if (isNotPrivilegedAndNotSerbia && personObject.tip_dokumenta !== "pasos") {
      setInputValue("tip_dokumenta", "pasos");
    }
  }, [personObject.drzavljanstvo]);

  console.log("personObject", personObject);

  const handleJmbgChange = (value) => {
    setInputValue("jmbg", value);
    if (value) {
      setInputValue("br_licne_karte", "");
      setInputValue("datum_rodjenja", "");
      setInputValue("zemlja_izdavanja", "");
      setInputValue("datum_izdavanja", "");
      setInputValue("datum_isteka", "");
    }
  };

  const handleIdNumberChange = (value) => {
    setInputValue("br_licne_karte", value);
    if (value || personObject.datum_rodjenja) {
      setInputValue("jmbg", "");
    }
  };

  const handleBirthDateChange = (value) => {
    setInputValue("datum_rodjenja", value);
    if (value || personObject.br_licne_karte) {
      setInputValue("jmbg", "");
    }
  };

  // useEffect(() => {
  //   console.log("slika", personObject.slika_crop);
  // }, [personObject]);

  const handleOnCropComplete = (img) => {
    setPersonObject((prev) => ({
      ...prev,
      slika_crop: img,
    }));
  };

  const fetchPicture = async () => {
    try {
      const result = await fetchData(
        apiRoutes.getPersonImage.replace(":id",personObject.id),
        showAlert,
        navigate,
        t,
      );

      if (!result) return;

      console.log("slika rezultat: ",result.data)

      return result.data.image;
    } catch (err) {
      console.error("Error fetching event:", err);
    }
  };

  const handleOnCropClick = async () => {
    try {
      setIsLoading(true);

      const image = await fetchPicture();
      if (!image) {
        setIsLoading(false);
        return;
      }

      setImageForCrop(image);
      setCropKey((prev) => prev + 1);
      setCropImage(true);
    } catch (err) {
      console.error("Error on crop click:", err);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <EditModalShell
        isLoading={imageLoading || isLoading}
        t={t}
        className="edit-modal-shell-reverse"
        headerInfo={
          editing &&
          (personObject?.korisnik ||
            personObject?.createdAt ||
            personObject?.updatedAt) ? (
            <RegisteredByLine
              korisnik={personObject.korisnik}
              createdAt={personObject.createdAt}
              updatedAt={personObject.updatedAt}
              t={t}
            />
          ) : (
            <p className="inria-sans-400 font-24 modal-title">
              {editing ? t("EDIT_PERSON_TITLE") : t("ADD_PERSON_TITLE")}
            </p>
          )
        }
        onSave={readOnly ? undefined : editing ? handleSaveClick : handleAddClick}
        onClose={onCloseClick}
        onDelete={
          editing && apiRoutes.deletePerson
            ? () => setIsConfirmDeleteOpen(true)
            : undefined
        }
        sidePanel={
          <>
            <div className="flex-direction-column gap-8 items-center width-100 person-photo-container">
              <div className="flex-direction-column gap-2 width-100 person-photo-container-header">
                <p className="font-16 inria-sans-700 person-photo-container-title">
                  {t("PHOTO") || "Fotografija"}
                </p>
                <p className="font-12 inria-sans-400 person-photo-container-hint">
                  {t("PHOTO_PRINT_SCAN_HINT") ||
                    "Fotografija se prikazuje na odštampanoj akreditaciji i koristi se za skeniranje."}
                </p>
              </div>
              <AddPicture
                setImageLoading={setImageLoading}
                setImagePreview={(value) => {
                  setInputValue("slika_crop", value);
                }}
                imagePreview={personObject.slika_crop}
                setInputValue={(value) => setInputValue("slika", value)}
                route={apiRoutes.validateImage}
                disabled={readOnly}
              />

              {personObject.slika_crop && crop && !readOnly && (
                <AccreditationButton
                  type="export"
                  title={t("CROP")}
                  icon={icons.edit_image}
                  dark_icon={icons.edit_image_dark}
                  hover_icon={icons.edit_image_blue}
                  onClick={() => {
                    handleOnCropClick();
                  }}
                />
              )}
            </div>
            {/* Verifiers (isSecondTierViewer) only ever have their own
                single vote — that's surfaced as the ACCEPT/REJECT status
                action above, not as a "predlozi" list. Predlozi (every
                verifier's vote) is a Third-only admin-review concept —
                gated on TIER (a build-time literal), not apiRoutes, so it's
                not just hidden but actually absent from First/Second's
                compiled bundle. Deliberately NOT gated on the broader
                `readOnly` — admins should still see every verifier's vote
                after a person is resolved, not just before. */}
            {editing &&
              TIER === "third" &&
              apiRoutes.updatePersonStatus &&
              !isSecondTierViewer && (
                <PredlogPanel predlozi={personObject.predlozi} t={t} />
              )}
            {/* First has no comment routes (addComment etc.) at all — gated
                on TIER so the panel is absent from First's bundle, not just
                unrendered. */}
            {editing && TIER !== "first" && apiRoutes.addComment && (
              <CommentsPanel
                id={object?.id}
                person={personObject}
                setPerson={setPersonObject}
                t={t}
              />
            )}
            {/* History is a Third-only (xsall) concept — see the comment atop
                HistoryPanel.jsx. Gated on TIER so it's absent from
                First/Second's bundle, not just unrendered. */}
            {editing && TIER === "third" && (
              <HistoryPanel tabela="osobe" redId={object?.id} />
            )}
          </>
        }
      >
            <div className="flex-direction-column gap-24 width-100 modal-content">
              {/* Accept/decline is a Second+Third concept — First has no
                  updatePersonStatus route, no voting. Gated on TIER so the
                  action bar is absent from First's bundle, not just
                  unrendered. */}
              {editing &&
                TIER !== "first" &&
                apiRoutes.updatePersonStatus &&
                (statusPillConfig ? (
                  <div
                    className="flex-direction-row items-center justify-center gap-8 pointer person-status-pill"
                    style={{ backgroundColor: statusPillConfig.bg }}
                    onClick={() => setIsStatusModalOpen(true)}
                  >
                    <img
                      src={statusPillConfig.icon}
                      alt={personObject.status}
                      width={20}
                      height={20}
                      style={{ filter: "brightness(0) invert(1)" }}
                    />
                    <span className="font-16 inria-sans-700 person-status-pill-text">
                      {statusPillConfig.label}
                    </span>
                  </div>
                ) : (
                  <div className="flex-direction-row person-status-split">
                    <button
                      className="person-status-half person-status-reject-half"
                      onClick={() =>
                        // Second's list already prompts for a comment before
                        // deciding status (EventSecondPage.jsx's status-cell
                        // click -> Status modal) — mirror that here instead of
                        // deciding instantly. Third keeps the instant click.
                        TIER === "second"
                          ? setIsStatusModalOpen(true)
                          : handleStatusChange("odbijen")
                      }
                    >
                      <img
                        src={icons.decline}
                        alt="reject"
                        width={20}
                        height={20}
                        style={{ filter: "brightness(0) invert(1)" }}
                      />
                      <span>{t("REJECT")}</span>
                    </button>
                    <button
                      className="person-status-half person-status-approve-half"
                      onClick={() =>
                        TIER === "second"
                          ? setIsStatusModalOpen(true)
                          : handleStatusChange("odobren")
                      }
                    >
                      <img
                        src={icons.accept}
                        alt="accept"
                        width={20}
                        height={20}
                        style={{ filter: "brightness(0) invert(1)" }}
                      />
                      <span>{t("ACCEPT")}</span>
                    </button>
                  </div>
                ))}
              <div className="flex-direction-row gap-48 width-100 modal-two-inputs-inline">
                <AccreditationInput
                  label={t("NAME")}
                  className="modal-input"
                  setInputValue={(value) => setInputValue("ime", value)}
                  value={personObject.ime}
                  error={errors?.ime}
                  disabled={readOnly}
                  required
                />
                <AccreditationInput
                  label={t("SURNAME")}
                  className="modal-input"
                  setInputValue={(value) => setInputValue("prezime", value)}
                  value={personObject.prezime}
                  error={errors?.prezime}
                  disabled={readOnly}
                  required
                />
              </div>
              <div className="width-100">
                <AccreditationInput
                  className="modal-input"
                  label={t("ENTITY")}
                  setInputValue={(value) => setInputValue("entitet", value)}
                  value={personObject.entitet}
                  disabled={readOnly}
                />
              </div>
              <div className="width-100">
                <AccreditationInput
                  className="modal-input"
                  label={t("POSITION")}
                  setInputValue={(value) => setInputValue("pozicija", value)}
                  value={personObject.pozicija}
                  disabled={readOnly}
                />
              </div>
              <div className="width-100">
                <AccreditationInput
                  className="modal-input"
                  label={t("EMAIL")}
                  setInputValue={(value) => setInputValue("email", value)}
                  value={personObject.email}
                  error={errors?.email}
                  disabled={readOnly}
                />
              </div>
              {countries && countries.length > 0 && (
                <AccreditationInput
                  type="dropdown"
                  label={t("CITIZENSHIP")}
                  className="modal-input"
                  searchable={true}
                  disabled={readOnly}
                  selectedOption={
                    countries.find(
                      (c) => c.country === personObject?.drzavljanstvo,
                    )?.name || ""
                  }
                  setSelectedOption={(value) => {
                    const selectedKey = countries?.find(
                      (option) => option.country === value,
                    )?.country;
                    setInputValue("drzavljanstvo", selectedKey);
                  }}
                  options={countries?.map((option) => ({
                    key: option.country,
                    value: option.name,
                    flag: option.flag,
                  }))}
                  flags
                  error={errors?.drzavljanstvo}
                  required
                />
              )}

              {personObject?.drzavljanstvo && (
                <div
                  className={`document-section ${
                    errors?.broj_dokumenta &&
                    personObject?.drzavljanstvo === "COUNTRY_SERBIA"
                      ? "error"
                      : ""
                  } relative`}
                >
                  {personObject?.drzavljanstvo === "COUNTRY_SERBIA" ? (
                    <div className="flex-direction-row gap-12 relative">
                      <div className="flex-direction-column gap-24 width-100">
                        <div
                          className={`width-100 input-block ${
                            personObject.jmbg ? "active" : ""
                          }`}
                        >
                          <AccreditationInput
                            label={t("JMBG")}
                            value={personObject.jmbg}
                            setInputValue={(value) => handleJmbgChange(value)}
                            disabled={
                              readOnly ||
                              personObject.br_licne_karte ||
                              personObject.datum_rodjenja
                            }
                            className="modal-input"
                            error={errors?.jmbg}
                          />
                        </div>

                        <div
                          className={`flex-direction-row gap-48 width-100 modal-two-inputs-inline`}
                        >
                          <AccreditationInput
                            label={t("ID_CARD_NUMBER")}
                            className="modal-input"
                            value={personObject.br_licne_karte}
                            setInputValue={(value) =>
                              handleIdNumberChange(value)
                            }
                            disabled={readOnly || personObject.jmbg}
                          />

                          <CustomDateRangePicker
                            label={t("DATE_OF_BIRTH")}
                            className="modal-input up-calendar"
                            mode="single"
                            dateRange={
                              personObject.datum_rodjenja
                                ? formatDateForInput(
                                    personObject.datum_rodjenja,
                                  )
                                : ""
                            }
                            setDateRange={(value) => {
                              setInputValue(
                                "datum_rodjenja",
                                new Date(value).toISOString(),
                              );
                            }}
                            disabled={readOnly || personObject.jmbg}
                            error={errors?.datum_rodjenja}
                          />
                        </div>
                      </div>
                      <div className="flex-direction-column relative  or-separator ">
                        <div></div>
                        <span className="font-12 inria-sans-700">
                          {t("OR")}
                        </span>

                        <div></div>
                      </div>
                    </div>
                  ) : (
                    <div className="flex-direction-row gap-48 width-100 modal-two-inputs-inline">
                      <AccreditationInput
                        type="dropdown"
                        label={t("DOCUMENT_TYPE")}
                        className="modal-input"
                        selectedOption={
                          PRIVILEGED_COUNTRIES.some((country) =>
                            personObject.drzavljanstvo
                              .toLowerCase()
                              ?.includes(country.toLowerCase()),
                          )
                            ? personObject?.tip_dokumenta?.replace(/_/g, " ")
                            : "pasos"
                        }
                        setSelectedOption={(value) => {
                          const selectedKey = ID_CARD_AND_PASSPORT.find(
                            (option) => option.key === value,
                          )?.value;
                          setInputValue("tip_dokumenta", selectedKey);
                        }}
                        options={ID_CARD_AND_PASSPORT.map((option) => ({
                          key: option.key,
                          value: option.label,
                        }))}
                        disabled={
                          readOnly ||
                          !PRIVILEGED_COUNTRIES.some((country) =>
                            personObject.drzavljanstvo
                              .toLowerCase()
                              ?.includes(country.toLowerCase()),
                          )
                        }
                        error={errors?.tip_dokumenta}
                        required={PRIVILEGED_COUNTRIES.some((country) =>
                          personObject.drzavljanstvo
                            .toLowerCase()
                            ?.includes(country.toLowerCase()),
                        )}
                      />
                      <AccreditationInput
                        label={t("DOCUMENT_NUMBER")}
                        className="modal-input"
                        setInputValue={(value) =>
                          setInputValue("broj_dokumenta", value)
                        }
                        value={personObject.broj_dokumenta}
                        error={errors?.broj_dokumenta}
                        disabled={readOnly}
                        required
                      />
                    </div>
                  )}
                </div>
              )}
              {countries &&
                countries.length > 0 &&
                personObject.drzavljanstvo &&
                (personObject.drzavljanstvo !== "COUNTRY_SERBIA" ||
                  (personObject.drzavljanstvo === "COUNTRY_SERBIA" &&
                    !personObject.jmbg &&
                    (personObject.br_licne_karte ||
                      personObject.datum_rodjenja))) && (
                  <AccreditationInput
                    type="dropdown"
                    label={t("ISSUING_COUNTRY")}
                    className="modal-input"
                    searchable={true}
                    disabled={readOnly}
                    selectedOption={
                      countries.find(
                        (c) => c.country === personObject?.zemlja_izdavanja,
                      )?.name || ""
                    }
                    setSelectedOption={(value) => {
                      const selectedKey = countries?.find(
                        (option) => option.country === value,
                      )?.country;
                      setInputValue("zemlja_izdavanja", selectedKey);
                    }}
                    options={countries?.map((option) => ({
                      key: option.country,
                      value: option.name,
                      flag: option.flag,
                    }))}
                    flags
                    error={errors?.zemlja_izdavanja}
                    required
                  />
                )}

              {personObject.drzavljanstvo &&
                (personObject.drzavljanstvo !== "COUNTRY_SERBIA" ||
                  (personObject.drzavljanstvo === "COUNTRY_SERBIA" &&
                    !personObject.jmbg &&
                    (personObject.br_licne_karte ||
                      personObject.datum_rodjenja))) && (
                  <div className="flex-direction-row gap-48 width-100 modal-two-inputs-inline">
                    <CustomDateRangePicker
                      label={t("DATE_OF_ISSUE")}
                      required
                      mode="single"
                      dateRange={
                        personObject.datum_izdavanja
                          ? formatDateForInput(personObject.datum_izdavanja)
                          : ""
                      }
                      setDateRange={(value) => {
                        setInputValue(
                          "datum_izdavanja",
                          new Date(value).toISOString(),
                        );
                      }}
                      disabled={readOnly}
                      error={errors?.datum_izdavanja}
                    />
                    <CustomDateRangePicker
                      label={t("EXPIRY_DATE")}
                      required
                      mode="single"
                      dateRange={
                        personObject.datum_isteka
                          ? formatDateForInput(personObject.datum_isteka)
                          : ""
                      }
                      setDateRange={(value) => {
                        setInputValue(
                          "datum_isteka",
                          new Date(value).toISOString(),
                        );
                      }}
                      disabled={readOnly}
                      error={errors?.datum_isteka}
                    />
                  </div>
                )}
              <div className="width-100"></div>
            </div>
      </EditModalShell>
      {cropImage && imageForCrop && (
        <CustomImageCropper
          localSrc={imageForCrop}
          key={cropKey}
          onCloseClick={() => setCropImage(false)}
          onCropComplete={(img) => {
            handleOnCropComplete(img);
          }}
        />
      )}
      {isStatusModalOpen && (
        <Modal
          // Second gets the comment-capable Status modal (matches the list's
          // status-cell click); Third keeps the plain ChangeStatus modal it
          // already had, unchanged.
          type={TIER === "second" ? "status" : "change-status"}
          statusChange={{
            id: personObject.id,
            status: personObject.status,
            fullName: `${personObject.ime} ${personObject.prezime}`,
          }}
          onCloseClick={() => setIsStatusModalOpen(false)}
          handleUpdateData={(value) => {
            setPersonObject((prev) => ({ ...prev, status: value.status }));
            handleUpdateData?.(value);
          }}
        />
      )}
      {isConfirmDeleteOpen && (
        <ConfirmationModal
          handleDeclineClick={() => setIsConfirmDeleteOpen(false)}
          handleConfirmClick={() => handleDeleteClick()}
          term={`${personObject.ime} ${personObject.prezime}`}
        />
      )}
      {isReapprovalConfirmOpen && (
        <ConfirmationModal
          handleDeclineClick={() => setIsReapprovalConfirmOpen(false)}
          handleConfirmClick={handleConfirmReapproval}
          question={t("CONFIRM_EDIT_APPROVED_PERSON_QUESTION")}
          term={`${personObject.ime} ${personObject.prezime}`}
          notification={"*" + t("EDIT_APPROVED_PERSON_WARNING_DETAILS")}
          warning
        />
      )}
    </>
  );
};

export default AddPerson;
